import gql from 'graphql-tag';
import z from 'zod';

import { client } from 'src/data/api';
import { hasValidationErrors } from 'src/utils/validation';
import { OrganizationRecord } from 'src/types';
import { organizationSchema } from './validation';

export const uploadOrgScopedLogo = async (
  organizationID: number,
  file: File,
): Promise<{ url: string; validationError?: never } | { url?: never; validationError: string }> => {
  const { data, errors } = await client.query<{
    generateOrganizationLogoUploadUrl: {
      url: string;
    };
  }>({
    query: gql`
      query generateOrganizationLogoUploadUrl($organizationID: Int!, $mimeType: String!) {
        generateOrganizationLogoUploadUrl(organizationID: $organizationID, mimeType: $mimeType) {
          url
        }
      }
    `,
    variables: { organizationID, mimeType: file.type },
    errorPolicy: 'all',
    context: {
      // it's important we disable `queryDeduplication`, as we might upload multiple files in
      // parallel
      queryDeduplication: false,
    },
  });

  if (errors) {
    const error = errors[0];
    if (hasValidationErrors(error)) {
      const validationError = error.extensions.validationErrors.map(({ message }) => message).join('. ');
      return { validationError };
    }

    throw error;
  }

  const fileUploadURL = data.generateOrganizationLogoUploadUrl.url;

  await fetch(fileUploadURL, { method: 'PUT', body: file });

  return { url: fileUploadURL.split('?')[0] };
};

interface FormFileUploadsResult {
  validationErrors?: Partial<Record<string, string>>;
  orgLogoURL: string | null;
  pdfLogoOverrideURL: string | null;
}

export const handleFormFiles = async (
  orgID: number,
  formValues: z.infer<typeof organizationSchema>,
): Promise<FormFileUploadsResult> => {
  const result: FormFileUploadsResult = {
    // default to value's `src`, which holds URL
    orgLogoURL: formValues.logo?.src || null,
    pdfLogoOverrideURL: formValues.settings.data.pdfLogoOverride?.src || null,
  };

  // `rawFile` is newly-added file
  if (!formValues.logo?.rawFile && !formValues.settings.data.pdfLogoOverride?.rawFile) {
    return result;
  }

  const promises: Promise<void>[] = [];
  const validationErrors: Partial<Record<string, string>> = {};

  if (formValues.logo?.rawFile) {
    promises.push(
      uploadOrgScopedLogo(orgID, formValues.logo.rawFile).then((res) => {
        if (res.validationError) validationErrors.logo = res.validationError;
        if (res.url) result.orgLogoURL = res.url;
      }),
    );
  }

  if (formValues.settings.data.pdfLogoOverride?.rawFile) {
    promises.push(
      uploadOrgScopedLogo(orgID, formValues.settings.data.pdfLogoOverride.rawFile).then((res) => {
        if (res.validationError) {
          validationErrors['settings.data.pdfLogoOverride'] = res.validationError;
        }
        if (res.url) result.pdfLogoOverrideURL = res.url;
      }),
    );
  }

  await Promise.all(promises);

  if (Object.keys(validationErrors).length) result.validationErrors = validationErrors;
  return result;
};

export const createOrganization = (orgParams: Record<string, unknown>) =>
  client.mutate<{
    createOrganization: {
      id: number;
    };
  }>({
    mutation: gql`
      mutation createOrganization($organization: CreateOrganizationInput!) {
        createOrganization(organization: $organization) {
          id
        }
      }
    `,
    variables: { organization: orgParams },
    errorPolicy: 'all',
  });

export const updateOrganization = (orgID: number, orgParams: Record<string, unknown>) =>
  client.mutate<{
    updateOrganization: boolean;
  }>({
    mutation: gql`
      mutation updateOrganization($id: Int!, $organization: UpdateOrganizationInput!) {
        updateOrganization(id: $id, organization: $organization)
      }
    `,
    variables: { id: orgID, organization: orgParams },
    errorPolicy: 'all',
  });

export const handleCriteriaGroupUpdates = async (
  orgID: number,
  existingOrgCriteriaGroups: OrganizationRecord['criteria_groups'],
  criteriaGroupIdsToEnable: string[],
) => {
  const orgGroupUpdateParamsArr: Array<{
    id: string;
    archived_at: string | null;
    updated_at?: string;
  }> = [];
  const groupIdsToAdd: string[] = [];

  existingOrgCriteriaGroups.forEach((existingOrgGroup) => {
    const currentlyEnabled = !existingOrgGroup.archived_at;
    const shouldEnable = criteriaGroupIdsToEnable.some((id) => id === existingOrgGroup.criteria_group_id);
    if (currentlyEnabled !== shouldEnable) {
      // Include all updatable fields except the nested criteria_group relation
      orgGroupUpdateParamsArr.push({
        id: existingOrgGroup.id,
        archived_at: shouldEnable ? null : 'now()',
        updated_at: 'now()',
      });
    }
  });

  criteriaGroupIdsToEnable.forEach((id) => {
    const existingOrgGroupRecord = existingOrgCriteriaGroups.find((grp) => grp.criteria_group_id === id);
    if (!existingOrgGroupRecord) groupIdsToAdd.push(id);
  });

  // If there are no updates to make, return early
  if (orgGroupUpdateParamsArr.length === 0 && groupIdsToAdd.length === 0) {
    return { data: null, errors: null };
  }

  return client.mutate({
    mutation: gql`
      mutation updateOrganizationCriteriaGroups(
        $newOrgGroups: [organizations_criteria_groups_insert_input!]!
        $orgGroupUpdateParamsArr: [organizations_criteria_groups_updates!]!
      ) {
        insert_organizations_criteria_groups(objects: $newOrgGroups) {
          affected_rows
        }
        update_organizations_criteria_groups_many(updates: $orgGroupUpdateParamsArr) {
          affected_rows
        }
      }
    `,
    variables: {
      newOrgGroups: groupIdsToAdd.map((id) => ({ organization_id: orgID, criteria_group_id: id })),
      orgGroupUpdateParamsArr: orgGroupUpdateParamsArr.map((orgGroup) => ({
        where: { id: { _eq: orgGroup.id } },
        _set: {
          archived_at: orgGroup.archived_at,
          updated_at: orgGroup.updated_at,
        },
      })),
    },
    errorPolicy: 'all',
  });
};

export const UPDATE_SEVERITY_CONFIG_MUTATION = gql`
  mutation UpdateSeverityConfig($organization_id: Int!, $criterion_id: uuid!, $severity: Int!) {
    insert_organizations_criteria_configs_one(
      object: { organization_id: $organization_id, criterion_id: $criterion_id, severity: $severity }
      on_conflict: { constraint: uk_organizations_criteria_configs_unique, update_columns: [severity, updated_at] }
    ) {
      id
      severity
      updated_at
    }
  }
`;

export const updateSeverityConfig = async (organizationId: number, criterionId: string, severity: number) =>
  client.mutate({
    mutation: UPDATE_SEVERITY_CONFIG_MUTATION,
    variables: {
      organization_id: organizationId,
      criterion_id: criterionId,
      severity,
    },
  });

export const UPDATE_INCLUDE_DP_CONFIG_MUTATION = gql`
  mutation UpdateIncludeDpConfig($organization_id: Int!, $criterion_id: uuid!, $include_dp: Boolean!) {
    insert_organizations_criteria_configs_one(
      object: { organization_id: $organization_id, criterion_id: $criterion_id, include_dp: $include_dp }
      on_conflict: { constraint: uk_organizations_criteria_configs_unique, update_columns: [include_dp, updated_at] }
    ) {
      id
      include_dp
      updated_at
    }
  }
`;

export const UPDATE_INCLUDE_GUP_CONFIG_MUTATION = gql`
  mutation UpdateIncludeGupConfig($organization_id: Int!, $criterion_id: uuid!, $include_gup: Boolean!) {
    insert_organizations_criteria_configs_one(
      object: { organization_id: $organization_id, criterion_id: $criterion_id, include_gup: $include_gup }
      on_conflict: { constraint: uk_organizations_criteria_configs_unique, update_columns: [include_gup, updated_at] }
    ) {
      id
      include_gup
      updated_at
    }
  }
`;

export const updateIncludeDpConfig = async (organizationId: number, criterionId: string, includeDp: boolean) =>
  client.mutate({
    mutation: UPDATE_INCLUDE_DP_CONFIG_MUTATION,
    variables: {
      organization_id: organizationId,
      criterion_id: criterionId,
      include_dp: includeDp,
    },
  });

export const updateIncludeGupConfig = async (organizationId: number, criterionId: string, includeGup: boolean) =>
  client.mutate({
    mutation: UPDATE_INCLUDE_GUP_CONFIG_MUTATION,
    variables: {
      organization_id: organizationId,
      criterion_id: criterionId,
      include_gup: includeGup,
    },
  });
